import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';

import CustomStore, { Options as StoreOptions } from 'devextreme/data/custom_store';
import DataSource, { Options as DataSourceOptions } from 'devextreme/data/data_source';
import { DevExtremeData, DxSortInfo, hasValue, LoadOptions } from '@my-aon/utils';
import { ENVIRONMENT } from '@environment';
import { dxDataGridColumn } from 'devextreme/ui/data_grid';
import { Operation as JsonPatchOperation } from 'rfc6902'

export const getParams = (loadOptions?: LoadOptions, queryParams: any | null = null): HttpParams => {
    let getValue = (value: any) => { return value instanceof Object ? JSON.stringify(value) : value };

    let keys = [
        "skip",
        "take",
        "requireTotalCount",
        "requireGroupCount",
        "sort",
        "filter",
        "totalSummary",
        "group",
        "groupSummary",
        "select",
        "isCountQuery"
    ];

    if (loadOptions && hasValue(loadOptions?.group)) loadOptions.requireGroupCount = true;

    if (loadOptions?.take === 0) loadOptions.isCountQuery = true;

    let params = new HttpParams();
    if (loadOptions && hasValue(loadOptions)) {
        Object.keys(loadOptions).forEach(key => {
            if (keys.indexOf(key) === -1)
                return;

            let value = (loadOptions as any)[key];

            if (!hasValue(value)) // null | undefined
                return;

            params = params.append(key, getValue(value));
        });
    }

    if (hasValue(queryParams)) {
        Object.keys(queryParams).forEach(key => {
            let value = queryParams[key];

            if (Array.isArray(value)) {
                value.forEach(x => {
                    params = params.append(`${key}`, getValue(x));
                })
            }
            else if (hasValue(value))
                params = params.append(key, getValue(value));
        });

    }

    return params;
}

export const normalizeSelect = (columns: any[], extra?: string[]): (string | undefined)[] => {
    return columns
        .map((x: string | dxDataGridColumn) => {
            return (typeof x === 'string') ? [x] : x.columns && x.columns.length > 0 && (x.columns as dxDataGridColumn[]).map((y: dxDataGridColumn) => y.dataField) || [x.dataField];
        })
        .reduce((all: (string | undefined)[], arr: (string | undefined)[]) => all.concat(arr))
        .concat(extra)
        .filter(x => !!x);
}

@Injectable({
    providedIn: 'root'
})
export class StoreService {

    readonly environment = inject(ENVIRONMENT);
    readonly path: string = `${this.environment.apiUrl}`;

    readonly http: HttpClient = inject(HttpClient);

    getURI = (url: string): string => (url.toLowerCase().startsWith('http') ? '' : (this.path + url));

    /**
     *
     * @param uri
     * @param loadOptions
     * @param queryParams
     * @param search
     * @param searchMode
     * @returns
     */
    load = (
        url: string,
        loadOptions: LoadOptions,
        queryParams: any | null = null,
        search?: string | any | null,
        searchMode?: '=' | '<>' | 'contains' | 'notcontains' | 'startswith' | 'endswith'
    ): Promise<DevExtremeData> => {

        loadOptions.requireTotalCount = true;
		let filter = [] as any[];

		if (loadOptions?.filter && loadOptions?.filter.length)
			filter.push(loadOptions.filter);

		if (!!loadOptions.searchExpr && !!loadOptions.searchOperation && !!loadOptions.searchValue) {
			let searchExprFilter = [loadOptions.searchExpr, loadOptions.searchOperation, loadOptions.searchValue]
			if (Array.isArray(loadOptions.searchExpr)) {
				searchExprFilter = [];
				loadOptions.searchExpr.forEach(search => {
					if (typeof (search) == 'string') {
						searchExprFilter.push([search, loadOptions.searchOperation, loadOptions.searchValue]);
					}
				});
				//@ts-ignore
				searchExprFilter = [].concat(...searchExprFilter.map(n => [n, 'or'])).slice(0, -1) // Interleave 'or'
			}
			filter.push(searchExprFilter);
		}

		if (hasValue(loadOptions.group) && typeof loadOptions.group === 'string') {
			//return firstValueFrom(of({data: [], totalCount: 0}));
			loadOptions.group = [{ selector: loadOptions.group, isExpanded: false } as any];
		}

		if (hasValue(loadOptions.group) && typeof loadOptions.group === 'object' && !Array.isArray(loadOptions.group)) {
			//return firstValueFrom(of({data: [], totalCount: 0}));
			loadOptions.group = [loadOptions.group as any];
		}

		if (hasValue(loadOptions.sort) && typeof loadOptions.sort === 'string') {
			//return firstValueFrom(of({data: [], totalCount: 0}));
			loadOptions.sort = [{ selector: loadOptions.sort, desc: false } as DxSortInfo];
		}

		if (hasValue(loadOptions.sort) && typeof loadOptions.sort === 'object' && !Array.isArray(loadOptions.sort)) {
			//return firstValueFrom(of({data: [], totalCount: 0}));
			loadOptions.sort = [loadOptions.sort as DxSortInfo];
		}

		let params = getParams(filter.length ? { ...loadOptions, filter } : loadOptions, queryParams);
		if (!!search) {
			params = params.set('search', search);

			if (!!searchMode)
				params = params.set('searchMode', searchMode);
		}

		const URI: string = this.getURI(url);
		return firstValueFrom(this.http.get<DevExtremeData>(URI, { params }));

    }



    /**
     *
     * @param url
     * @param key
     * @param byKey
     * @param insert
     * @param update
     * @param remove
     * @param queryParams
     * @param loadMode
     * @param cacheRawData
     * @returns
     */
    CustomStore<TModel = any>(
        url: string,
        key: string | Array<string>,
        options: {
            byKey?: ((key: string | Array<string>) => PromiseLike<any>),
            insert?: ((values: any) => PromiseLike<any>),
            update?: ((key: string | Array<string>, values: any) => PromiseLike<any>),
            remove?: ((key: string | Array<string>) => PromiseLike<void>),
            queryParams?: any | null,
            loadMode?: 'processed' | 'raw',
            cacheRawData?: boolean
        }

    ): CustomStore<TModel> {

        let { byKey, insert, update, remove, queryParams, loadMode, cacheRawData } = options ?? {};

        return new CustomStore<TModel, string>({
            key: key,
            load: (loadOptions: LoadOptions): Promise<DevExtremeData<TModel>> => this.load(url, loadOptions, queryParams),
            byKey,
            insert,
            update,
            remove,
            loadMode: loadMode ?? 'processed',
            cacheRawData: cacheRawData ?? true
        });
    }


    /**
     *
     * @param url
     * @param key
     * @param options
     * @returns
     */
    Store<TModel = any>(
        url: string,
        key?: string | Array<string>,
        options?: {
            queryParams?: any | null,
            search?: string | any | null,
            searchMode?: '=' | '<>' | 'contains' | 'notcontains' | 'startswith' | 'endswith',
            onLoaded?: ((result: DevExtremeData | Array<any>) => any | any[]) | null,
            onLoading?: ((loadOptions: LoadOptions) => void),
            loadMode?: 'processed' | 'raw'
        }

    ): CustomStore<TModel> {

        let { queryParams, search, searchMode, onLoaded, onLoading, loadMode } = options ?? {};

        const uri: string = `${url.toLowerCase().startsWith('http') ? '' : this.path}${url}`;

        const byKey = (keyValue: string | Array<string>): PromiseLike<TModel> => {
            let params = getParams({ filter: [key, keyValue], take: 1, requireTotalCount: true }, queryParams);
            return firstValueFrom(this.http.get<any>(uri, { params }).pipe(map(result => result.data && result.data.length && result.data[0])));
        }
        const insert = (values: any): PromiseLike<any> => firstValueFrom(this.http.post(uri, values, { params: queryParams }));
        const update = (keyValue: string | Array<string>, values: any): PromiseLike<any> => {
            let patches: JsonPatchOperation[] = Object.keys(values).map(k => {
                let value: string | number | boolean = values[k];
                if (hasValue(value)) return { op: 'replace', path: k, value };
                else return { op: 'remove', path: k };
            })

            return firstValueFrom(this.http.patch(uri + '/' + keyValue, patches, { params: queryParams }));
        };
        const remove = (key: string | Array<string>): PromiseLike<void> => firstValueFrom(this.http.delete<void>(uri + '/' + key, { params: queryParams }));

        return new CustomStore<TModel, string>({
            key: key,
            load: (loadOptions: LoadOptions): Promise<DevExtremeData<TModel>> => this.load(url, loadOptions, queryParams, search, searchMode),
            byKey,
            update,
            insert,
            remove,
            onLoaded,
            onLoading,
            loadMode
        } as StoreOptions);
    }


    /**
     *
     * @param url
     * @param key
     * @param options
     * @returns
     */
    DataSource<TModel = any>(
        url: string,
        key: string | any | null,
        options?: {
            filter?: any[] | string | null,
            group?: string | any | null,
            sort?: string | DxSortInfo[] | null,
            select?: string | any | any[] | dxDataGridColumn[] | null,
            pageSize?: number,
            onLoaded?: ((result: DevExtremeData | Array<any>) => any | any[]) | null,
            onLoading?: ((loadOptions: LoadOptions) => void),
            queryParams?: any | null,
            search?: string | any | null,
            searchMode?: '=' | '<>' | 'contains' | 'notcontains' | 'startswith' | 'endswith',
            loadMode?: 'processed' | 'raw'
        }

    ): DataSource<TModel> { return new DataSource<TModel>(this.DataSourceOptions<TModel>(url, key, options)); }

    /**
     *
     * @param url
     * @param key
     * @param options
     * @returns
     */
    DataSourceOptions<TModel = any>(
        url: string,
        key: string | any,
        options?: {
            filter?: any[] | string | null,
            group?: string | any | null,
            sort?: string | DxSortInfo[] | null,
            select?: string | any | any[] | dxDataGridColumn[] | null,
            pageSize?: number,
            onLoaded?: ((result: DevExtremeData | Array<any>) => any | any[]) | null,
            onLoading?: ((loadOptions: LoadOptions) => void),
            queryParams?: any | null,
            search?: string | any | null,
            searchMode?: '=' | '<>' | 'contains' | 'notcontains' | 'startswith' | 'endswith',
            loadMode?: 'processed' | 'raw'
        }
    ): DataSourceOptions<TModel> {

        let { filter, group, sort, select, pageSize, onLoaded: onLoaded, onLoading, queryParams, search, searchMode, loadMode } = options ?? {};

        if (filter && filter.length === 0)
            filter = null;

        if (sort && typeof sort === 'string')
            sort = [{ selector: sort, desc: false }];

        if (sort && typeof sort === 'object' && !Array.isArray(sort))
            sort = [sort];

        if (group && typeof group === 'string')
            group = [{ selector: group }];

        if (group && typeof group === 'object' && !Array.isArray(group))
            group = [group];

        if (select && Array.isArray(select))
            select = normalizeSelect(select);

        return {
            store: this.Store<TModel>(url, key, { queryParams, search, searchMode, onLoaded, onLoading, loadMode }),
            filter: filter,
            group: group,
            sort: sort as any[],
            select: select,
            pageSize: pageSize ?? undefined,
            paginate: !!pageSize,
            requireTotalCount: true,

        };
    }

}
